import React, { useEffect } from 'react';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { Divider, Skeleton, Space, Typography } from 'antd';
import { openDesktopApplication } from '../../Addins/addin';
import { useAppStore } from '../../common/store';
import { SignIntoShareFileModalExtensionSlotName } from '../../constants';
import { CoreExtensionSlotParams } from '../../types';
import { getMicrosoftOfficeAppName, t } from '../../utils';
import { ModalFooter } from './ModalFooter';

const { Text, Link, Title } = Typography;

interface EditDesktopAppModalBodyProps {
	app: PiletApi;
	cancelInteraction?: () => void;
	completeInteraction?: () => void;
}

export function EditDesktopAppModalTitle() {
	const { documentTemplate } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
	}));
	const fileName = documentTemplate?.file?.fileName;
	return (
		<Title level={3}>
			{t('edit.editDesktopTitle', {
				appType: getMicrosoftOfficeAppName(fileName),
			})}
		</Title>
	);
}

export function EditDesktopAppModalBody({
	app,
	cancelInteraction,
	completeInteraction,
}: EditDesktopAppModalBodyProps) {
	const {
		setEditDesktopModalCloseHandlers,
		documentTemplate,
		setIsSignedInToShareFileLinkClicked,
	} = useAppStore(state => ({
		setEditDesktopModalCloseHandlers: state.setEditDesktopModalCloseHandlers,
		documentTemplate: state.documentTemplate,
		setIsSignedInToShareFileLinkClicked: state.setIsSignedInToShareFileLinkClicked,
	}));

	useEffect(() => {
		if (cancelInteraction && completeInteraction) {
			setEditDesktopModalCloseHandlers({
				cancelInteraction,
				completeInteraction,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fileName = documentTemplate?.file?.fileName;

	const openSignIntoShareFileModal = () => {
		app.showModal(SignIntoShareFileModalExtensionSlotName);
	};

	const onLinkClick = () => {
		setIsSignedInToShareFileLinkClicked(true);
		setTimeout(() => {
			openSignIntoShareFileModal();
		}, 1000);
		completeInteraction?.();
	};

	return (
		<Text>
			<Text strong>{fileName}</Text>
			{t('edit.editDesktopOpenMessage', {
				appType: getMicrosoftOfficeAppName(fileName),
			})}
			<Divider
				style={{
					marginTop: 10,
					marginBottom: 10,
				}}
			/>
			<Space direction="vertical" size="middle" style={{ display: 'flex' }}>
				<div>
					{t('edit.editTroubleText')}
					<Link onClick={onLinkClick}>
						<u>{t('edit.editDesktopConfirmMessage')}</u>
					</Link>
				</div>
			</Space>
		</Text>
	);
}

export function EditDesktopAppModalFooter() {
	const { editDesktopModalCloseHandlers } = useAppStore(state => ({
		editDesktopModalCloseHandlers: state.editDesktopModalCloseHandlers,
	}));

	const tryAgain = async () => {
		openDesktopApplication();
	};

	return (
		<ModalFooter
			onPrimaryButtonClick={() => {
				editDesktopModalCloseHandlers?.completeInteraction();
			}}
			onDefaultButtonClick={tryAgain}
			primaryText={t('edit.closeText')}
			defaultText={t('edit.tryAgainText')}
		/>
	);
}

const LazyEditDesktopAppModalBody = React.lazy(() =>
	import('./EditDesktopAppModal').then(module => ({
		default: module['EditDesktopAppModalBody'],
	}))
);

export const AsyncLazyEditDesktopAppModalBody: React.FC<
	CoreExtensionSlotParams<any>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyEditDesktopAppModalBody {...props} />
		</React.Suspense>
	);
};
